/**
 * TuiTui Channel Plugin for OpenClaw.
 *
 * Implements the ChannelPlugin interface following the Synology Chat pattern.
 */
import { DEFAULT_ACCOUNT_ID } from 'openclaw/plugin-sdk/account-id';
import { setAccountEnabledInConfigSection, deleteAccountFromConfigSection, OpenClawPluginApi } from 'openclaw/plugin-sdk/core';
import { CHANNEL_ID, CHANNEL_NAME } from "./const";
import { version } from "../package.json"
import { handleInboundMessage } from './inbound';
import { guessChatTypeV2 } from "./chat_base"
import {
  sendTextMsg,
  sendMediaMsg,
} from "./outbound";
import { createTuituiClient } from './sdk';
import { channelConfigs } from '../openclaw.plugin.json';
import { isEnabled } from './utils';
import { dmPolicyDefault, resolveAccount, getAccountInfo } from "./accounts"

const isConfigured = (account: any)=> !!(account?.appId && account?.appSecret);

function createTuiTuiChannelPlugin(apiRuntime: any) {
  return {
    id: CHANNEL_ID,
    meta: {
      id: CHANNEL_ID,
      label: CHANNEL_NAME,
      selectionLabel: CHANNEL_NAME,
      detailLabel: CHANNEL_NAME + " " + version,
      docsPath: `/channels/${CHANNEL_ID}`,
      blurb: `Connect to ${CHANNEL_NAME} bot via WebSocket`,
      order: 100,
    },
    /*** 能力清单配置 ***/
    capabilities: {
      chatTypes: ['direct' as const, 'group' as const],
      media: true,
      threads: false,
      reactions: false,
      edit: false,
      unsend: false,
      reply: true,
      effects: false,
      blockStreaming: false,
    },
    configSchema: channelConfigs.tuitui,
    reload: { configPrefixes: [`channels.${CHANNEL_ID}`] },

    config: {
      listAccountIds: (cfg: any) => {
        const base = cfg?.channels?.[CHANNEL_ID];
        const ids = new Set<string>();
        ids.add(DEFAULT_ACCOUNT_ID);
        if (base?.accounts) for (const k in base.accounts) ids.add(k);
        return Array.from(ids);
      },

      resolveAccount,

      defaultAccountId: () => DEFAULT_ACCOUNT_ID,

      isEnabled: (account: any) => isEnabled(account?.enabled),

      isConfigured,

      describeAccount: (account: any) => ({
        accountId: account?.accountId || DEFAULT_ACCOUNT_ID,
        enabled: isEnabled(account?.enabled),
        configured: isConfigured(account),
      }),

      setAccountEnabled: ({ cfg, accountId, enabled }: any) => {
        return accountId === DEFAULT_ACCOUNT_ID ? {
            ...cfg,
            channels: {
              ...cfg.channels,
              [CHANNEL_ID]: { ...(cfg.channels?.[CHANNEL_ID] ?? {}), enabled },
            },
          } : setAccountEnabledInConfigSection({
            cfg,
            enabled,
            accountId,
            sectionKey: CHANNEL_ID,
            allowTopLevel: true,
          });
      },

      deleteAccount: ({ cfg, accountId }: any) => {
        return accountId === DEFAULT_ACCOUNT_ID ? {
            ...cfg,
            channels: {
              ...cfg.channels,
              [CHANNEL_ID]: {
                ...(cfg.channels?.[CHANNEL_ID] ?? {}),
                ...getAccountInfo(), // 重置基础配置字段，恢复默认值
                enabled: false, // 默认账户不删除，改为禁用，并重置配置字段
              },
            },
          } : deleteAccountFromConfigSection({ cfg, accountId, sectionKey: CHANNEL_ID }); // 多账户状态下的配置信息，按 accountId 删除指定账户; 除非子账户影响根账户字段信息，否则不应该使用 clearBaseFields
      },
    },

    pairing: {
      idLabel: 'tuituiUserId',
      normalizeAllowEntry: (entry: string) => entry.toLowerCase().trim(),
    },

    status: {
      defaultRuntime: {
        accountId: DEFAULT_ACCOUNT_ID,
        running: false,
        lastStartAt: null,
        lastStopAt: null,
        lastError: null,
      },
      buildAccountSnapshot: (params: any) => {
        const account = params.account;
        return {
          ...params.runtime,
          accountId: params.accountId || account?.accountId || DEFAULT_ACCOUNT_ID,
          enabled: isEnabled(account?.enabled),
          configured: isConfigured(account),
        };
      },
    },

    security: {
      resolveDmPolicy: ({ cfg, accountId, account }: { cfg: any; accountId?: string | null; account?: any; }) => {
        account = account || resolveAccount(cfg, accountId);
        const accId = accountId ?? account.accountId ?? DEFAULT_ACCOUNT_ID;
        let allowFromPath =`channels.${CHANNEL_ID}.`;
        if (accId !== DEFAULT_ACCOUNT_ID) allowFromPath += `accounts.${accId}.`;

        const policy = account.dmPolicy ?? dmPolicyDefault;
        // dmPolicy semantics:
        // - open: always allow everyone (["*"]), ignore allowFrom values.
        // - pairing: unknown senders get a pairing code; approvals add to allowFrom store.
        // - allowlist: only allow explicitly listed users in allowFrom.
        // - disabled: block all DMs.
        return {
          policy,
          allowFrom: policy === 'open' ? ['*'] : (account.allowFrom ?? []),
          policyPath: `${allowFromPath}dmPolicy`,
          allowFromPath,
          approveHint: `当前 ${CHANNEL_ID} openclaw（AccountId: ${accountId}）需要配对校验, code: <code>`,
          normalizeEntry: (raw: string) => raw.toLowerCase().trim(),
        };
      },
    },

    messaging: {
      normalizeTarget: (target: string) => target.trim(),
      targetResolver: {
        looksLikeId: (id: string) => !!id && id.trim().length > 0,
        hint: "<userId>",
      },
    },

    directory: {
      self: async () => null,
      listPeers: async () => [],
      listGroups: async () => [],
    },

    outbound: {
      deliveryMode: 'direct' as const,
      textChunkLimit: 10000,  // API上限制为50k

      sendText: async ({ cfg, to, text, accountId, replyToId, threadId }: any) => {
        console.log(`[${CHANNEL_ID}] sendText to=${to}, text=${text}, accountId=${accountId}`);
        const account = resolveAccount(cfg, accountId);
        
        const {chatId, chatType} = await guessChatTypeV2(account, to);
        
        console.log(`[${CHANNEL_ID}] AccountId ${accountId} outbound.sendText() ${chatType} to ${chatId} ${text}`);

        await sendTextMsg(account, chatId, chatType, text);

        return { channel: CHANNEL_ID, messageId: `tuitui-text-${Date.now()}`, chatId };
      },

      sendMedia: async ({ cfg, to, mediaUrl, accountId, account }: any) => {
        console.log(`[${CHANNEL_ID}] sendMedia to=${to}, mediaUrl=${mediaUrl}, accountId=${accountId}`);
        account = account || resolveAccount(cfg, accountId);

        const {chatId, chatType} = await guessChatTypeV2(account, to);
        console.log(`[${CHANNEL_ID}] sendMediaMsg guess chatId=${chatId}, chatType=${chatType}`);
        await sendMediaMsg(account, chatId, chatType, mediaUrl);

        return { channel: CHANNEL_ID, messageId: `tuitui-media-${Date.now()}`, chatId };
      },
    },

    agentPrompt: {
      messageToolHints: () => [
        '- 在推推私聊或群聊中，如果需要用户确认权限、授权继续、校验身份、确认危险操作、从多个选项中选择、或填写信息，应主动优先使用 `tuitui_bot_quick_card` 发送交互式卡片，不要只用纯文本追问。',
        '- `tuitui_bot_quick_card` 适合 confirm/select/multi-input/form；只有需要完全自定义 interactive JSON 时才使用 `tuitui_bot_send_card`。',
        '- Teams 团队频道/帖子不支持交互式卡片；在 Teams 场景必须改用文字消息或频道帖子，不要调用卡片工具。',
      ],
    },

    gateway: {
      startAccount: async (ctx: any) => {
        const { cfg, accountId, log, abortSignal, setStatus, getStatus } = ctx;
        const _setStatus = (obj = {}) => {
          if (!setStatus || !getStatus) return;
          setStatus({ ...getStatus(), running: false, connected: false, ...obj });
        };
        const account = resolveAccount(cfg, accountId);

        if (!isEnabled(account.enabled)) {
          log?.info?.(`[${CHANNEL_ID}] AccountId ${accountId} is disabled, skipping`);
          return _setStatus();
        }

        if (!isConfigured(account)) {
          log?.warn?.(`[${CHANNEL_ID}] AccountId ${accountId} not fully configured (missing appId/appSecret)`);
          return _setStatus();
        }

        log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, Starting TuiTui channel`);
        _setStatus({ running: true });

        let closed = false;
        log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, starting SDK WebSocket subscription`);

        const subscription = createTuituiClient(account, log).event.subscribe({
          onConnected: () => {
            if (closed) return;
            log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, WebSocket connect success`);
            _setStatus({ running: true, connected: true });
          },
          onDisconnected: (reason) => {
            if (closed) return;
            log?.info?.(`[${CHANNEL_ID}] AccountId: ${accountId}, WebSocket disconnected`, reason);
            _setStatus({ running: true, connected: false });
          },
          onError: (err) => {
            if (closed) return;
            log?.warn?.(`[${CHANNEL_ID}] AccountId: ${accountId}, WebSocket error: ${err.message}`);
          },
          onEvent: async (body) => {
            if (closed) return;
            log?.debug?.(`[${CHANNEL_ID}] AccountId: ${accountId}, received event ${body.event}`);
            _setStatus({ running: true, connected: true, lastInboundAt: Date.now() });
            if (apiRuntime) {
              await handleInboundMessage({ body, account, apiRuntime, log });
            } else {
              log?.error?.(`[${CHANNEL_ID}] AccountId: ${accountId}, WebSocket.onInbounMessage TuiTuiRuntime error`);
            }
          },
        });

        const closeSubscription = () => {
          if (closed) return;
          closed = true;
          subscription.unsubscribe();
        };

        // 保持账户运行状态，直至触发“abortSignal”信号为止。
        await new Promise<void>((resolve) => {
          const _onAbort = () => {
            log?.info?.(`[${CHANNEL_ID}] AccountId ${accountId} stopping (abort signal)`);
            closeSubscription();
            _setStatus({ running: false, connected: false });
            resolve();
          };
          if (abortSignal?.aborted) return _onAbort();
          abortSignal?.addEventListener("abort", _onAbort, { once: true });
        });
      },

      stopAccount: async (ctx: any) => {
        ctx.log?.info?.(`[${CHANNEL_ID}] AccountId ${ctx.accountId} stopped`);
        ctx.setStatus?.({ ...ctx.getStatus?.(), running: false, connected: false });
      },
    },
  };
}

export function registerTuiTuiChannel(api: OpenClawPluginApi) {
  api.registerChannel({ plugin: createTuiTuiChannelPlugin(api.runtime) });
}
